import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { AIChatProvider } from './context/AIChatContext';
import { ThemeProvider } from './context/ThemeContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout/Layout';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import Tenants from './pages/Tenants';
import Companies from './pages/Companies';
import CompanyForm from './pages/CompanyForm';
import ProjectForm from './pages/ProjectForm';
import CampaignForm from './pages/CampaignForm';
import Projects from './pages/Projects';
import Campaigns from './pages/Campaigns';
import ActiveCampaigns from './pages/ActiveCampaigns';
import Settings from './pages/Settings';
import AuditLogs from './pages/AuditLogs';
import MarketingDashboard from './pages/MarketingDashboard';
import ContentManager from './pages/ContentManager';
import MarketingAnalytics from './pages/MarketingAnalytics';
import AutomationDashboard from './pages/AutomationDashboard';
import EmailMarketing from './pages/EmailMarketing';
import LandingPageGenerator from './pages/LandingPageGenerator';
import Integrations from './pages/Integrations';
import MarketplaceManager from './pages/MarketplaceManager';
import VoiceAI from './pages/VoiceAI';
import SalesSignals from './pages/SalesSignals';
import GeoMarketing from './pages/GeoMarketing';
import DigitalTwin from './pages/DigitalTwin';
import ProductResearch from './pages/ProductResearch';
import SEOAudit from './pages/SEOAudit';
import BlogAutomation from './pages/BlogAutomation';
import Products from './pages/Products';
import ProductForm from './pages/ProductForm';
import ProductCategories from './pages/ProductCategories';
import VatRates from './pages/VatRates';
import VatRateForm from './pages/VatRateForm';
import Currencies from './pages/Currencies';
import CurrencyForm from './pages/CurrencyForm';
import Countries from './pages/Countries';
import CountryForm from './pages/CountryForm';
import ContractTemplates from './pages/ContractTemplates';
import ContractTemplateForm from './pages/ContractTemplateForm';
import Contracts from './pages/Contracts';
import ContractDetail from './pages/ContractDetail';
import ContractForm from './pages/ContractForm';
import Coupons from './pages/Coupons';
import CouponForm from './pages/CouponForm';
import Orders from './pages/Orders';
import OrderForm from './pages/OrderForm';
import Customers from './pages/Customers';
import ContentTemplates from './pages/ContentTemplates';
import Leads from './pages/Leads';
import LeadForm from './pages/LeadForm';
import CustomerForm from './pages/CustomerForm';
import AutomationRules from './pages/AutomationRules';
import AutomationRuleForm from './pages/AutomationRuleForm';
import ABTests from './pages/ABTests';
import AIActions from './pages/AIActions';
import AIActionForm from './pages/AIActionForm';
import AIActionFormEdit from './pages/AIActionForm'; // Assuming reuse or same component
// import AIAgentForm from './pages/AIAgentForm'; // Keeping for now if needed for low-level config
import AIProviderForm from './pages/AIProviderForm';
import DocumentLibrary from './pages/DocumentLibrary';
import MarketplaceChannels from './pages/MarketplaceChannels';
import MarketplaceChannelForm from './pages/MarketplaceChannelForm';
import MarketplaceIntegrationForm from './pages/MarketplaceIntegrationForm';
import GlobalStyles from './styles/GlobalStyles';

// Product Configurators
import Configurators from './pages/Configurators';
import ConfiguratorForm from './pages/ConfiguratorForm';
import PipelineSettings from './pages/PipelineSettings';
import OrdersPipeline from './pages/OrdersPipeline';
import ConfiguratorAnalytics from './pages/ConfiguratorAnalytics';
import EmailTemplatesEditor from './pages/EmailTemplatesEditor';
import ConfiguratorSettings from './pages/ConfiguratorSettings';

// AI Studio
import ImageGenerationStudio from './pages/ImageGenerationStudio';
import VideoGenerationStudio from './pages/VideoGenerationStudio';
import ContentPipeline from './pages/ContentPipeline';
import MediaLibrary from './pages/MediaLibrary';
import AIProviderSettings from './pages/AIProviderSettings';

// Sales Radar
import {
  Dashboard as SalesRadarDashboard,
  LeadDetail as SalesRadarLeadDetail,
  LeadList as SalesRadarLeadList,
  PipelineKanban,
  ScoringRules,
  StageManagement,
  AIInsightsPanel,
  ReportBuilder as SalesRadarReportBuilder,
  WebhookManagement,
  HubSpotIntegration,
  SalesforceIntegration,
} from './pages/SalesRadar';

// New AI Components
import AICommandCenter from './pages/AICommandCenter';
import CampaignWizard from './components/wizards/CampaignWizard';
import ContentWizard from './components/wizards/ContentWizard';
import CustomerLifecycleDashboard from './pages/CustomerLifecycleDashboard';
import MarketingCalendar from './pages/MarketingCalendar';

// Content Pipeline
import ContentLibrary from './pages/ContentLibrary';
import DeploymentCalendar from './pages/DeploymentCalendar';
import DeploymentAnalytics from './pages/DeploymentAnalytics';

// New Feature Pages
import MarketingCrew from './pages/MarketingCrew';
import VIPProgram from './pages/VIPProgram';
import EventMarketing from './pages/EventMarketing';
import PaidAds from './pages/PaidAds';
import SMSMarketing from './pages/SMSMarketing';
import ContentManagement from './pages/ContentManagement';
import EcommerceAnalytics from './pages/EcommerceAnalytics';
import ChatDashboard from './pages/ChatDashboard';
import LandingPage from './pages/LandingPage';
import PublicConfigurator from './pages/PublicConfigurator';

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <AIChatProvider>
          <GlobalStyles />
          <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          {/* Public Configurator (no auth required) */}
          <Route path="/configurator/:slug" element={<PublicConfigurator />} />
          <Route element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }>
          <Route path="dashboard" element={<Dashboard />} />

          {/* Core Entities */}
          <Route path="users" element={<Users />} />
          <Route path="tenants" element={<Tenants />} />
          <Route path="companies" element={<Companies />} />
          <Route path="companies/new" element={<CompanyForm />} />
          <Route path="companies/:id/edit" element={<CompanyForm />} />

          {/* Projects & Campaigns */}
          <Route path="companies/:companyId/projects" element={<Projects />} />
          <Route path="companies/:companyId/projects/new" element={<ProjectForm />} />
          <Route path="companies/:companyId/projects/:projectId/edit" element={<ProjectForm />} />
          <Route path="companies/:companyId/projects/:projectId/campaigns" element={<Campaigns />} />
          <Route path="companies/:companyId/projects/:projectId/campaigns/new" element={<CampaignForm />} />
          <Route path="companies/:companyId/projects/:projectId/campaigns/:campaignId/edit" element={<CampaignForm />} />
          
          {/* Active Campaigns Overview */}
          <Route path="campaigns" element={<ActiveCampaigns />} />

          {/* Digital Twin */}
          <Route path="companies/:companyId/digital-twin" element={<DigitalTwin />} />

          {/* Hierarchical Marketing Dashboards */}
          <Route path="companies/:companyId/marketing" element={<MarketingDashboard />} />
          <Route path="companies/:companyId/projects/:projectId/marketing" element={<MarketingDashboard />} />

          {/* AI Command Center & Wizards */}
          <Route path="ai-command-center" element={<AICommandCenter />} />
          <Route path="ai/wizards/campaign" element={<CampaignWizard />} />
          <Route path="ai/wizards/content" element={<ContentWizard />} />
          <Route path="ai/wizards/research" element={<ProductResearch />} /> {/* Reusing existing page for now */}
          <Route path="ai/wizards/seo" element={<SEOAudit />} /> {/* Reusing existing page for now */}

          {/* AI Studio */}
          <Route path="ai-studio" element={<Navigate to="/ai-studio/image-generation" replace />} />
          <Route path="ai-studio/image-generation" element={<ImageGenerationStudio />} />
          <Route path="ai-studio/video-generation" element={<VideoGenerationStudio />} />
          <Route path="ai-studio/content-pipeline" element={<ContentPipeline />} />
          <Route path="ai-studio/media-library" element={<MediaLibrary />} />
          <Route path="ai-studio/provider-settings" element={<AIProviderSettings />} />

          {/* Content Pipeline */}
          <Route path="content-library" element={<ContentLibrary />} />
          <Route path="deployment-calendar" element={<DeploymentCalendar />} />
          <Route path="deployment-analytics" element={<DeploymentAnalytics />} />

          {/* Marketing Tools */}
          <Route path="marketing" element={<MarketingDashboard />} />
          <Route path="marketing-calendar" element={<MarketingCalendar />} />
          <Route path="content-manager" element={<Navigate to="/content-management" replace />} />
          <Route path="email-marketing" element={<EmailMarketing />} />
          <Route path="marketing-analytics" element={<MarketingAnalytics />} />
          <Route path="automation" element={<AutomationDashboard />} />
          <Route path="landing-pages" element={<LandingPageGenerator />} />
          <Route path="lifecycle" element={<CustomerLifecycleDashboard />} />

          {/* Advanced Features */}
          <Route path="integrations" element={<Integrations />} />
          <Route path="integrations/ai-providers/new" element={<AIProviderForm />} />
          <Route path="integrations/ai-providers/:id/edit" element={<AIProviderForm />} />
          <Route path="integrations/marketplace/new" element={<MarketplaceIntegrationForm />} />
          <Route path="integrations/marketplace/:id/edit" element={<MarketplaceIntegrationForm />} />
          <Route path="marketplace/channels" element={<MarketplaceChannels />} />
          <Route path="marketplace/channels/new" element={<MarketplaceChannelForm />} />
          <Route path="marketplace/channels/:id/edit" element={<MarketplaceChannelForm />} />
          <Route path="marketplace" element={<MarketplaceManager />} />
          <Route path="voice-ai" element={<VoiceAI />} />
          <Route path="sales-signals" element={<SalesSignals />} />
          
          {/* Sales Radar */}
          <Route path="sales-radar" element={<SalesRadarDashboard />} />
          <Route path="sales-radar/leads" element={<SalesRadarLeadList />} />
          <Route path="sales-radar/leads/:leadId" element={<SalesRadarLeadDetail />} />
          <Route path="sales-radar/pipeline" element={<PipelineKanban />} />
          <Route path="sales-radar/scoring" element={<ScoringRules />} />
          <Route path="sales-radar/stages" element={<StageManagement />} />
          <Route path="sales-radar/insights" element={<AIInsightsPanel />} />
          <Route path="sales-radar/reports" element={<SalesRadarReportBuilder />} />
          <Route path="sales-radar/webhooks" element={<WebhookManagement />} />
          <Route path="sales-radar/integrations/hubspot" element={<HubSpotIntegration />} />
          <Route path="sales-radar/integrations/salesforce" element={<SalesforceIntegration />} />
          
          <Route path="geo-marketing" element={<GeoMarketing />} />
          <Route path="digital-twin" element={<DigitalTwin />} />
          <Route path="product-research" element={<ProductResearch />} />
          <Route path="seo-audit" element={<SEOAudit />} />
          <Route path="blog-automation" element={<BlogAutomation />} />

          {/* New Feature Pages */}
          <Route path="marketing-crew" element={<MarketingCrew />} />
          <Route path="vip-program" element={<VIPProgram />} />
          <Route path="event-marketing" element={<EventMarketing />} />
          <Route path="paid-ads" element={<PaidAds />} />
          <Route path="sms-marketing" element={<SMSMarketing />} />
          <Route path="content-management" element={<ContentManagement />} />
          <Route path="ecommerce-analytics" element={<EcommerceAnalytics />} />
          <Route path="chat" element={<ChatDashboard />} />

          {/* Commerce */}
          <Route path="products" element={<Products />} />
          <Route path="products/new" element={<ProductForm />} />
          <Route path="products/:productId/edit" element={<ProductForm />} />
          <Route path="product-categories" element={<ProductCategories />} />
          <Route path="vat-rates" element={<VatRates />} />
          <Route path="vat-rates/new" element={<VatRateForm />} />
          <Route path="vat-rates/:vatRateId/edit" element={<VatRateForm />} />
          <Route path="currencies" element={<Currencies />} />
          <Route path="currencies/new" element={<CurrencyForm />} />
          <Route path="currencies/:id/edit" element={<CurrencyForm />} />
          <Route path="countries" element={<Countries />} />
          <Route path="countries/new" element={<CountryForm />} />
          <Route path="countries/:id/edit" element={<CountryForm />} />
          
          {/* Contract Management */}
          <Route path="contracts" element={<Contracts />} />
          <Route path="contracts/new" element={<ContractForm />} />
          <Route path="contracts/:id" element={<ContractDetail />} />
          <Route path="contracts/:id/edit" element={<ContractForm />} />
          <Route path="contracts/templates" element={<ContractTemplates />} />
          <Route path="contracts/templates/new" element={<ContractTemplateForm />} />
          <Route path="contracts/templates/:id" element={<ContractTemplateForm />} />
          <Route path="contracts/templates/:id/edit" element={<ContractTemplateForm />} />
          
          {/* Product Configurators */}
          <Route path="configurators" element={<Configurators />} />
          <Route path="configurators/new" element={<ConfiguratorForm />} />
          <Route path="configurators/:id" element={<ConfiguratorForm />} />
          <Route path="configurators/pipeline-settings" element={<PipelineSettings />} />
          <Route path="configurators/orders" element={<OrdersPipeline />} />
          <Route path="configurators/analytics" element={<ConfiguratorAnalytics />} />
          <Route path="configurators/email-templates" element={<EmailTemplatesEditor />} />
          <Route path="configurators/settings" element={<ConfiguratorSettings />} />
          
          <Route path="coupons" element={<Coupons />} />
          <Route path="coupons/new" element={<CouponForm />} />
          <Route path="coupons/:couponId/edit" element={<CouponForm />} />
          <Route path="orders" element={<Orders />} />
          <Route path="orders/new" element={<OrderForm />} />
          <Route path="orders/:orderId/edit" element={<OrderForm />} />
          <Route path="leads" element={<Leads />} />
          <Route path="leads/new" element={<LeadForm />} />
          <Route path="leads/:leadId/edit" element={<LeadForm />} />
          <Route path="customers" element={<Customers />} />
          <Route path="customers/new" element={<CustomerForm />} />
          <Route path="customers/:customerId/edit" element={<CustomerForm />} />

          {/* Configuration */}
          <Route path="settings" element={<Settings />} />
          <Route path="audit-logs" element={<AuditLogs />} />
          <Route path="content-templates" element={<ContentTemplates />} />
          <Route path="automation-rules" element={<AutomationRules />} />
          <Route path="automation-rules/new" element={<AutomationRuleForm />} />
          <Route path="automation-rules/:ruleId/edit" element={<AutomationRuleForm />} />
          <Route path="ab-tests" element={<ABTests />} />

          {/* Legacy/Low-level AI Config */}
          <Route path="ai-actions" element={<AIActions />} />
          <Route path="ai-actions/new" element={<AIActionForm />} />
          <Route path="ai-actions/:id/edit" element={<AIActionFormEdit />} />
          <Route path="projects/:projectId/documents" element={<DocumentLibrary />} />
          <Route path="documents" element={<DocumentLibrary />} />
        </Route>
      </Routes>
        </AIChatProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;